import { createFileRoute, Link } from '@tanstack/react-router';
import { useAuthStore } from '../lib/auth-store';
import { useStudentProgress } from '../lib/landings';
import { EmptyState } from '../components/ui/EmptyState';
import {
  LandingPage,
  KPIRow,
  CardsRow,
  LandingSkeleton,
  LandingError,
  LandingGuard,
  KPICard,
  LandingCard,
  LandingCardLine,
  ModuleGatedCard,
} from '../components/portal-shell';
import { formatINR, timeAgo } from '../lib/format';

export const Route = createFileRoute('/_authed/progress')({
  component: ProgressPage,
});

function ProgressPage() {
  return (
    <LandingGuard landingPath="/progress">
      <ProgressContent />
    </LandingGuard>
  );
}

const timeFormatter = new Intl.DateTimeFormat('en-IN', {
  weekday: 'short',
  hour: 'numeric',
  minute: '2-digit',
  hour12: false,
});

const fmtClassTime = (iso: string): string => {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return timeFormatter.format(d);
};

function ProgressContent() {
  const user = useAuthStore((s) => s.user);
  const query = useStudentProgress({ enabled: !!user });

  if (query.isLoading || (!query.data && !query.isError)) {
    return (
      <LandingPage
        title="My Progress"
        meta={user ? user.displayName : undefined}
        intro="How you're doing this month, with games and your referral card."
      >
        <LandingSkeleton />
      </LandingPage>
    );
  }
  if (query.isError || !query.data) {
    return (
      <LandingPage title="My Progress">
        <LandingError error={query.error} onRetry={() => query.refetch()} />
      </LandingPage>
    );
  }

  const { data } = query.data;
  const marksAverage =
    data.recentMarks && data.recentMarks.length > 0
      ? Math.round(
          (data.recentMarks.reduce((sum, m) => sum + (m.score / m.maxScore) * 100, 0) /
            data.recentMarks.length),
        )
      : null;

  return (
    <LandingPage
      title="My Progress"
      meta={user ? user.displayName : undefined}
      intro="How you're doing this month, with games and your referral card."
    >
      <KPIRow>
        <KPICard
          label="Attendance"
          value={`${data.attendancePercent30d}%`}
          footnote="Last 30 days"
          tone={
            data.attendancePercent30d >= 85
              ? 'success'
              : data.attendancePercent30d >= 60
                ? 'default'
                : 'warning'
          }
        />
        <KPICard
          label="Recent marks"
          value={marksAverage !== null ? `${marksAverage}%` : '—'}
          footnote={
            data.recentMarks === null
              ? 'Exams module not enabled yet'
              : data.recentMarks.length > 0
                ? `Across ${data.recentMarks.length} ${data.recentMarks.length === 1 ? 'result' : 'results'}`
                : 'No results yet'
          }
          newFor="/me/marks"
        />
        <KPICard
          label="Quiz streak"
          value={data.quizStreak ? `${data.quizStreak.currentStreakWeeks} wks` : '—'}
          footnote={
            data.quizStreak === null
              ? 'Games module not enabled yet'
              : typeof data.quizStreak.lastQuizScore === 'number'
                ? `Last score ${data.quizStreak.lastQuizScore}`
                : 'No quizzes taken yet'
          }
          newFor="/games"
        />
        <KPICard
          label="Wallet"
          value={data.walletBalance ? formatINR(data.walletBalance.balancePaise) : '—'}
          footnote={
            data.walletBalance === null
              ? 'Wallet not enabled yet'
              : 'Earn more by inviting a friend'
          }
          newFor="/me/referrals"
        />
      </KPIRow>

      <CardsRow>
        {data.quizStreak ? (
          <LandingCard title="This week's quiz" newFor="/games">
            <LandingCardLine>
              <span className="font-medium">Current streak:</span>{' '}
              <span className="tabular-nums">
                {data.quizStreak.currentStreakWeeks}{' '}
                {data.quizStreak.currentStreakWeeks === 1 ? 'week' : 'weeks'}
              </span>
            </LandingCardLine>
            <LandingCardLine>
              {typeof data.quizStreak.lastQuizScore === 'number' ? (
                <>
                  Last score:{' '}
                  <span className="tabular-nums font-medium">
                    {data.quizStreak.lastQuizScore}%
                  </span>
                </>
              ) : (
                <span className="text-[color:var(--color-text-muted)]">
                  Take this week's quiz to start a streak.
                </span>
              )}
            </LandingCardLine>
            <LandingCardLine last>
              <Link
                to="/games"
                className="inline-flex items-center gap-1 text-sm font-semibold text-gold-700 hover:text-gold-600 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gold-500"
              >
                Play this week's quiz →
              </Link>
            </LandingCardLine>
          </LandingCard>
        ) : (
          <ModuleGatedCard
            title="This week's quiz"
            description="Weekly quizzes and streaks land here once the Games module is enabled."
          />
        )}

        {data.walletBalance ? (
          <LandingCard title="Invite a friend" newFor="/me/referrals">
            <LandingCardLine>
              Current balance:{' '}
              <span className="font-medium tabular-nums">
                {formatINR(data.walletBalance.balancePaise)}
              </span>
            </LandingCardLine>
            <LandingCardLine last>
              When a friend pays their first month, ₹500 lands in your wallet.
            </LandingCardLine>
          </LandingCard>
        ) : (
          <ModuleGatedCard
            title="Invite a friend"
            description="Earn wallet credits when friends join. Coming once Referrals is enabled."
          />
        )}

        <LandingCard title="Recent teacher remark">
          {data.recentTeacherRemark ? (
            <>
              <LandingCardLine>
                <em>"{data.recentTeacherRemark.note}"</em>
              </LandingCardLine>
              <LandingCardLine last>
                <span className="text-[color:var(--color-text-muted)]">
                  {timeAgo(data.recentTeacherRemark.occurredAt)}
                </span>
              </LandingCardLine>
            </>
          ) : (
            <EmptyState
              title="No recent remark"
              description="Teacher feedback will appear here as it's recorded."
              className="py-6"
            />
          )}
        </LandingCard>

        <LandingCard title="Upcoming classes">
          {data.upcomingClasses.length > 0 ? (
            data.upcomingClasses.slice(0, 4).map((c, idx, arr) => (
              <LandingCardLine key={c.id} last={idx === arr.length - 1}>
                <span className="tabular-nums font-medium">
                  {fmtClassTime(c.scheduledAt)}
                </span>{' '}
                — <span>{c.batchName}</span>
                {c.lessonTitle ? (
                  <span className="text-[color:var(--color-text-muted)]">
                    {' '}
                    · {c.lessonTitle}
                  </span>
                ) : null}
              </LandingCardLine>
            ))
          ) : (
            <EmptyState
              title="No upcoming classes"
              description="Your next scheduled class will show up here."
              className="py-6"
            />
          )}
        </LandingCard>

        {data.recentMarks && data.recentMarks.length > 0 ? (
          <LandingCard title="Recent marks" newFor="/me/marks" className="md:col-span-2">
            {data.recentMarks.slice(0, 6).map((m, idx, arr) => (
              <LandingCardLine key={`${m.subject}-${m.recordedAt}-${idx}`} last={idx === arr.length - 1}>
                <span className="font-medium">{m.subject}</span>{' '}
                <span className="tabular-nums">
                  {m.score}/{m.maxScore}
                </span>
                <span className="text-[color:var(--color-text-muted)]">
                  {' '}
                  · {timeAgo(m.recordedAt)}
                </span>
              </LandingCardLine>
            ))}
          </LandingCard>
        ) : data.recentMarks === null ? (
          <ModuleGatedCard
            title="Recent marks"
            variant="full"
            description="Exam results will appear here once the Exams module is enabled."
          />
        ) : null}
      </CardsRow>
    </LandingPage>
  );
}
